import {
  Box,
  Checkbox,
  FormControl,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from "@mui/material";
import * as React from "react";
import { EnumProducts } from "services/caju/enums/enumProducts";
import { EnumInterestPlan } from "services/caju/enums/enumInterestPlan";
import { FormikProps } from "formik";
import { IStep3FormValues } from "../useStep3Form/iStep3FormValues";
import { MultibenefitsModal } from "../multibenefitsModal";
import { CorpExpensesModal } from "../corpExpensesModal";
import { AwardsModal } from "../awardsModal";
import { CajuMaisModal } from "../cajuMaisModal";
import { CajuCiclosModal } from "../cajuCiclosModal";
import { RebateModal } from "../rebateModal";
import { TagManagerService } from "services/tagManager";
import { EnumGTMEvents } from "services/tagManager/enums/enumGTMEvents";
import { EnumLabelTag } from "services/tagManager/enums/enumLabelTag";
import { listProducts } from "./listProducts";
import { FreshOfferLine } from "@caju-beneficios/react-icons";
import { TagStatus } from "components/TagStatus";

export interface PositionSelectProps {
  form: FormikProps<IStep3FormValues>;
  onBlur: (event: React.FocusEvent) => void;
}

export function ProductList(props: PositionSelectProps) {
  const [multiBenefitsModalVisible, setMultiBenefitsModalVisible] =
    React.useState(false);
  const [corpExpensesModalVisible, setCorpExpensesModalVisible] =
    React.useState(false);
  const [awardsModalVisible, setAwardsModalVisible] = React.useState(false);
  const [cajuMaisVisible, setCajuMaisVisible] = React.useState(false);
  const [cajuCiclosVisible, setCajuCiclosVisible] = React.useState(false);
  const [rebateModalVisible, setRebateModalVisible] = React.useState(false);

  function handleProductClick(value: EnumProducts) {
    if (!props.form.touched.selectedProducts) {
      props.form.setFieldTouched("selectedProducts");
    }

    const isCurrentlySelected =
      props.form.values.selectedProducts.includes(value);

    const newSelectedProducts = isCurrentlySelected
      ? props.form.values.selectedProducts.filter((p) => p !== value)
      : [...props.form.values.selectedProducts, value];

    props.form.setFieldValue("selectedProducts", newSelectedProducts);
  }

  function isChecked(value: EnumProducts) {
    return props.form.values.selectedProducts.includes(value);
  }

  const handleModalOpen = {
    [EnumProducts.MULTIBENEFITS]: () => setMultiBenefitsModalVisible(true),
    [EnumProducts.CORP_EXPENSES]: () => setCorpExpensesModalVisible(true),
    [EnumProducts.AWARDS]: () => setAwardsModalVisible(true),
    [EnumProducts.CAJU_MAIS]: () => setCajuMaisVisible(true),
    [EnumProducts.CAJU_CICLOS]: () => setCajuCiclosVisible(true),
    [EnumProducts.BENEFITS_WITH_WELLHUB]: () => setRebateModalVisible(true),
  };

  const handleModalOpenEvent = (labelTag: EnumLabelTag, id?: EnumProducts) => {
    if (id) handleModalOpen[id]();

    TagManagerService.pushLabelTag(
      EnumGTMEvents.PRODUCT_INFO_PRODUCT_INFO_TAG_TAG_DETAIL__CLICK,
      {
        label_tag: labelTag,
      }
    );
  };

  return (
    <React.Fragment>
      <MultibenefitsModal
        open={multiBenefitsModalVisible}
        onClose={() => setMultiBenefitsModalVisible(false)}
      />
      <CorpExpensesModal
        open={corpExpensesModalVisible}
        onClose={() => setCorpExpensesModalVisible(false)}
      />
      <AwardsModal
        open={awardsModalVisible}
        onClose={() => setAwardsModalVisible(false)}
      />
      <CajuMaisModal
        open={cajuMaisVisible}
        onClose={() => setCajuMaisVisible(false)}
      />
      <CajuCiclosModal
        open={cajuCiclosVisible}
        onClose={() => setCajuCiclosVisible(false)}
      />
      <RebateModal
        open={rebateModalVisible}
        onClose={() => setRebateModalVisible(false)}
      />

      <FormControl
        fullWidth
        error={!!props.form.errors.selectedProducts}
        onBlur={props.onBlur}
      >
        <List
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {listProducts.map((product, index) => (
            <ListItem
              key={index}
              sx={{
                borderWidth: isChecked(product.id) ? 2 : 1,
                borderColor: isChecked(product.id)
                  ? "primary.main"
                  : "medium.main",
                borderStyle: "solid",
                borderRadius: "16px",
              }}
              disablePadding
            >
              <ListItemButton
                onClick={() => handleProductClick(product.id)}
                dense
                sx={{
                  p: 2,
                }}
              >
                <ListItemIcon sx={{ minWidth: "auto", marginRight: 1 }}>
                  <Checkbox
                    edge="start"
                    checked={isChecked(product.id)}
                    tabIndex={-1}
                    disableRipple
                    value={product.id}
                    inputProps={{ "aria-labelledby": product.id }}
                  />
                </ListItemIcon>
                {"tag" in product ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      flex: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          fontSize: "16px",
                          letterSpacing: "0.08px",
                        }}
                      >
                        {product.title}
                      </Typography>
                      <TagStatus variant="green" icon={<FreshOfferLine />}>
                        {product.tag}
                      </TagStatus>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#4d4d4d",
                        fontSize: "14px",
                        letterSpacing: "0.07px",
                      }}
                    >
                      {product.description}
                    </Typography>
                  </Box>
                ) : (
                  <ListItemText
                    id={product.id}
                    primary={product.title}
                    secondary={product.description}
                  />
                )}
                <ListItemSecondaryAction
                  style={{
                    position: "initial",
                    top: 0,
                    transform: "none",
                    marginLeft: 8,
                  }}
                >
                  <span
                    className="uil uil-info-circle"
                    onClick={(evt) => {
                      evt.stopPropagation();
                      handleModalOpenEvent(product.labelTag, product.id);
                    }}
                    style={{
                      fontSize: 20,
                      color: "#E80537",
                      cursor: "pointer",
                    }}
                  />
                </ListItemSecondaryAction>
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Typography variant="subtitle1" sx={{ mb: 2, mt: 3 }}>
          Quer contratar nosso Plano Essencial?
        </Typography>

        <List>
          <ListItem
            sx={{
              borderWidth: props.form.values.interestPlan.length > 0 ? 2 : 1,
              borderColor:
                props.form.values.interestPlan.length > 0
                  ? "primary.main"
                  : "medium.main",
              borderStyle: "solid",
              borderRadius: "16px",
            }}
            disablePadding
          >
            <ListItemButton
              onClick={() => {
                if (!props.form.touched.interestPlan) {
                  props.form.setFieldTouched("interestPlan");
                }

                const newValues =
                  props.form.values.interestPlan.length > 0
                    ? []
                    : [EnumInterestPlan.BASIC_PLAN];
                props.form.setFieldValue("interestPlan", newValues);
              }}
              dense
              sx={{
                p: 2,
              }}
            >
              <ListItemIcon sx={{ minWidth: "auto", marginRight: 1 }}>
                <Checkbox
                  edge="start"
                  checked={props.form.values.interestPlan.length > 0}
                  tabIndex={-1}
                  disableRipple
                  value={EnumInterestPlan.BASIC_PLAN}
                  inputProps={{
                    "aria-labelledby": EnumInterestPlan.BASIC_PLAN,
                  }}
                />
              </ListItemIcon>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  flex: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      fontSize: "16px",
                      letterSpacing: "0.08px",
                    }}
                  >
                    Plano essencial
                  </Typography>
                  <TagStatus variant="green">100% Grátis</TagStatus>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#4d4d4d",
                    fontSize: "14px",
                    letterSpacing: "0.07px",
                  }}
                >
                  Gestão unificada de Benefícios, Despesas, Premiações e
                  Pessoas. Ganhe mais eficiência e autonomia com o nosso plano
                  100% gratuito.
                </Typography>
              </Box>
            </ListItemButton>
          </ListItem>
        </List>
      </FormControl>
    </React.Fragment>
  );
}
